import React, { useState, useEffect } from 'react';
import { 
  Calendar, CheckCircle, PencilSimple, ArrowsClockwise,
  Clock, User, Hash, Lightning, X, CaretDown, CaretUp,
  Copy, CheckFat, Warning, Sparkle, Export, ListChecks, Spinner,
  VideoCamera, Microphone, Play
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { videoAPI } from '@/utils/api';
import { computeGenerateVideoGate } from './shared/dfyVideoGate';
import { VideoWorkflowActions, useVideoWorkflow } from './shared/VideoWorkflow';
import { NEXT_ACTION, getVideoNextAction } from './shared/videoWorkflow';

/**
 * WeeklyContentPlanner - Generate and manage weekly social media content
 * For internal accounts: Founder (14 posts) + PayGBot (7 posts) = 21 posts/week
 */

const DAY_THEMES = {
  Monday: { name: 'Plan', color: 'blue', icon: '📋' },
  Tuesday: { name: 'Problem', color: 'red', icon: '🔍' },
  Wednesday: { name: 'Process', color: 'purple', icon: '⚙️' },
  Thursday: { name: 'Example', color: 'green', icon: '💡' },
  Friday: { name: 'Lesson', color: 'orange', icon: '📚' },
  Saturday: { name: 'Tip', color: 'teal', icon: '✨' },
  Sunday: { name: 'Review', color: 'pink', icon: '📊' }
};

const ACCOUNT_COLORS = {
  founder: 'from-purple-500/20 to-pink-500/20 border-purple-500/30',
  paygbot: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30'
};

const ACCOUNT_LABELS = {
  founder: { name: 'Founder', badge: 'bg-purple-500/30 text-purple-300' },
  paygbot: { name: 'PayGBot', badge: 'bg-blue-500/30 text-blue-300' }
};

// Single Post Card Component
const STATUS_PILL = (s) => {
  const map = {
    ready: 'bg-green-500/20 text-green-300 border-green-500/30',
    attached: 'bg-green-500/20 text-green-300 border-green-500/30',
    not_required: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    generating: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    missing: 'bg-red-500/20 text-red-300 border-red-500/30',
    failed: 'bg-red-500/20 text-red-300 border-red-500/30',
    non_public: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  return map[s] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
};

const PostReadinessSection = ({ post }) => {
  const r = post.readiness;
  if (!r) return null;
  return (
    <div
      data-testid={`post-readiness-${post.id}`}
      className={`border rounded-lg p-3 space-y-3 ${
        r.is_ready_to_approve
          ? 'border-green-500/30 bg-green-500/5'
          : 'border-yellow-500/30 bg-yellow-500/5'
      }`}
    >
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/80">
          Finished Asset
        </label>
        <span
          data-testid={`post-readiness-overall-${post.id}`}
          className={`text-xs px-2 py-0.5 border rounded-full ${
            r.is_ready_to_approve
              ? STATUS_PILL('ready')
              : STATUS_PILL('generating')
          }`}
        >
          {r.is_ready_to_approve ? 'Ready to approve' : r.approve_button_label || 'Not ready'}
        </span>
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1">
          <span className="text-slate-400">Video</span>
          <span
            data-testid={`post-readiness-video-${post.id}`}
            className={`px-2 py-0.5 border rounded ${STATUS_PILL(r.video_status)}`}
          >
            {r.video_status}
          </span>
        </div>
        <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1">
          <span className="text-slate-400">Voice</span>
          <span
            data-testid={`post-readiness-audio-${post.id}`}
            className={`px-2 py-0.5 border rounded ${STATUS_PILL(r.audio_status)}`}
          >
            {r.audio_status}
          </span>
        </div>
        <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1">
          <span className="text-slate-400">Caption</span>
          <span
            data-testid={`post-readiness-caption-${post.id}`}
            className={`px-2 py-0.5 border rounded ${STATUS_PILL(r.caption_status)}`}
          >
            {r.caption_status}
          </span>
        </div>
        <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1">
          <span className="text-slate-400">Public URL</span>
          <span
            data-testid={`post-readiness-url-${post.id}`}
            className={`px-2 py-0.5 border rounded ${STATUS_PILL(r.final_url_status)}`}
          >
            {r.final_url_status}
          </span>
        </div>
      </div>

      {/* Final video preview when available */}
      {r.preview_video_url ? (
        <div data-testid={`post-video-preview-${post.id}`}>
          <video
            src={r.preview_video_url}
            controls
            preload="metadata"
            className="w-full rounded-lg bg-black"
          />
          <a
            href={r.preview_video_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`post-video-link-${post.id}`}
            className="block mt-1 text-xs text-blue-300 hover:text-blue-200 truncate"
          >
            {r.preview_video_url}
          </a>
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic" data-testid={`post-no-preview-${post.id}`}>
          {r.is_video_post
            ? 'Video preview will appear here once generation + voiceover complete.'
            : 'No video preview required for this post.'}
        </p>
      )}

      {/* Blocked reasons */}
      {!r.is_ready_to_approve && (r.blocked_reasons || []).length > 0 && (
        <ul
          data-testid={`post-blocked-reasons-${post.id}`}
          className="text-xs text-red-300 list-disc list-inside space-y-0.5"
        >
          {r.blocked_reasons.map((reason, idx) => (
            <li key={idx}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
};


// Round 71/73 — Voiceover preview panel on Weekly Planner cards. Uses the
// SAME proven endpoint Create Content Bot uses (`videoAPI.previewVoiceover`
// → POST /api/videos/voiceover-preview), then persists the result onto the
// planner post via the existing PUT /weekly-content/post endpoint. No
// duplicate Weekly-Planner-only voiceover route.
const VoiceoverPreviewPanel = ({ post, onPreviewed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playbackError, setPlaybackError] = useState(false);

  const stamped = post.voice_preview || null;
  const hasPreview = Boolean(stamped?.audio_url && stamped?.audio_duration);
  const apiBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const audioFullUrl = hasPreview && stamped.audio_url.startsWith('/')
    ? `${apiBackend}${stamped.audio_url}`
    : stamped?.audio_url;

  const handlePreview = async () => {
    if (loading) return;
    const cleanScript = (post.script || '').trim();
    if (!cleanScript) {
      const msg = 'Script is required before previewing the voiceover.';
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true);
    setError('');
    setPlaybackError(false);
    try {
      // 1) Generate the preview using the EXACT same API path Create
      //    Content uses. Proven working on production.
      // Round 84 — backend now returns final_script + script_was_shortened
      // when audio > 15s triggers sentence-aware rewrite. We persist both
      // the voice_preview AND the rewritten script so the planner UI no
      // longer shows a 37-word script next to a 9-second audio clip.
      const previewResp = await videoAPI.previewVoiceover({ script: cleanScript });

      const voicePreview = {
        audio_url: previewResp.audio_url,
        audio_duration: previewResp.audio_duration,
        script_hash: previewResp.script_hash,
        cached: Boolean(previewResp.cached),
        previewed_at: new Date().toISOString(),
      };

      const scriptWasShortened = Boolean(previewResp.script_was_shortened);
      const persistPayload = {
        post_id: post.id,
        voice_preview: voicePreview,
      };
      if (scriptWasShortened && previewResp.final_script) {
        persistPayload.script = previewResp.final_script;
      }

      // 2) Persist the preview (and the shortened script if any) onto
      //    the planner post via the SAME PUT endpoint we use for
      //    caption/hook/script edits.
      try {
        await api.put('/weekly-content/post', persistPayload);
      } catch (stampErr) {
        console.warn('[WeeklyPlanner] voice_preview stamp failed:', stampErr);
      }

      toast.success(
        scriptWasShortened
          ? `Script was shortened to fit 15s (${previewResp.audio_duration?.toFixed?.(1) || '?'}s).`
          : previewResp.cached
            ? `Using cached voiceover preview (free) — ${previewResp.audio_duration?.toFixed?.(1) || '?'}s.`
            : `Voiceover preview loaded (${previewResp.audio_duration?.toFixed?.(1) || '?'}s).`,
      );
      onPreviewed?.(post.id, voicePreview, scriptWasShortened ? previewResp.final_script : null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : err?.message || 'Voiceover preview failed. Please check voice settings or try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const scriptForPreview = (post.script || '').trim();
  const canPreview = scriptForPreview.length > 0 && !loading;

  return (
    <div
      data-testid={`voiceover-preview-panel-${post.id}`}
      className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Microphone size={16} className="text-violet-300" />
          <span className="text-xs font-medium text-violet-200 uppercase tracking-wider">
            Voiceover preview
          </span>
        </div>
        <button
          type="button"
          data-testid={`preview-voiceover-btn-${post.id}`}
          onClick={handlePreview}
          disabled={!canPreview}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${
            !canPreview
              ? 'bg-slate-700/60 text-slate-500 cursor-not-allowed border border-slate-600/40'
              : 'bg-violet-600 hover:bg-violet-500 text-white shadow-md shadow-violet-500/20'
          }`}
        >
          {loading ? (
            <>
              <Spinner size={14} className="animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Play size={14} weight="fill" />
              <span>{hasPreview ? 'Preview again' : 'Preview voiceover'}</span>
            </>
          )}
        </button>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed">
        Hear the TTS voice before spending Runway credits. Calls ElevenLabs only —
        <span className="text-white"> zero Runway credits</span>. Required before Generate Video.
      </p>
      {hasPreview && (
        <div className="pt-1 space-y-1" data-testid={`voiceover-preview-player-wrapper-${post.id}`}>
          <audio
            key={audioFullUrl}
            controls
            preload="auto"
            src={audioFullUrl}
            data-testid={`voiceover-preview-player-${post.id}`}
            className="w-full h-10"
            onError={() => setPlaybackError(true)}
            onCanPlay={() => setPlaybackError(false)}
          >
            Your browser does not support audio playback.
          </audio>
          {playbackError ? (
            <p className="text-[11px] text-rose-400">Preview audio could not be loaded. Tap Preview again.</p>
          ) : (
            <p
              className="text-[11px] text-emerald-400"
              data-testid={`voiceover-preview-duration-${post.id}`}
            >
              {Number(stamped.audio_duration).toFixed(1)}s · ElevenLabs voiceover ready
            </p>
          )}
        </div>
      )}
      {error && !loading && (
        <p className="text-[11px] text-rose-400" data-testid={`voiceover-preview-error-${post.id}`}>
          {error}
        </p>
      )}
    </div>
  );
};


const PostCard = ({ post, onApprove, onEdit, onCopy, onGenerateVideo, onRetryVideo, onPreviewVoice, expanded, onToggleExpand, onVideoPromptRegenerated, isInternalAccount = false, v2EnvAvailable = false, onGenerateVideoV2 = null, onRetryVideoV2 = null, v2JobPostId = null, voicePreviewCache = {} }) => {
  const [copied, setCopied] = useState(false);
  const [regeneratingPrompt, setRegeneratingPrompt] = useState(false);
  const theme = DAY_THEMES[post.day] || { color: 'slate', icon: '📝' };
  const accountStyle = ACCOUNT_COLORS[post.account] || ACCOUNT_COLORS.founder;
  const accountLabel = ACCOUNT_LABELS[post.account] || ACCOUNT_LABELS.founder;

  const handleCopy = () => {
    const fullText = `${post.hook}\n\n${post.script}\n\n${post.caption}\n\n${post.hashtags.join(' ')}`;
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
    onCopy?.(post);
  };

  const handleCopyVideoPrompt = (e) => {
    e.stopPropagation();
    if (!post.video_prompt) return;
    navigator.clipboard.writeText(post.video_prompt);
    toast.success('Video prompt copied!');
  };

  const handleRegenerateVideoPrompt = async (e) => {
    e.stopPropagation();
    if (regeneratingPrompt) return;
    setRegeneratingPrompt(true);
    try {
      const res = await api.post(`/weekly-content/post/${post.id}/regenerate-video-prompt`);
      const data = res.data || {};
      if (data.success && data.video_prompt) {
        onVideoPromptRegenerated?.(post.id, data.video_prompt, data.video_prompt_alt);
        toast.success('Video prompt generated.');
      } else {
        toast.error('Could not generate a video prompt.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Request failed';
      toast.error(`Failed: ${detail}`);
    } finally {
      setRegeneratingPrompt(false);
    }
  };

  return (
    <motion.div
      layout
      className={`bg-gradient-to-br ${accountStyle} border rounded-xl overflow-hidden`}
      data-testid={`post-card-${post.id}`}
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => onToggleExpand(post.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className={`px-2 py-1 rounded-md text-xs font-medium ${accountLabel.badge}`}>
              {accountLabel.name}
            </span>
            <span className="text-white/60 text-sm">{post.post_time}</span>
            <span className="text-xl">{theme.icon}</span>
          </div>
          <div className="flex items-center space-x-2">
            {post.approved && (
              <CheckCircle size={18} className="text-green-400" weight="fill" />
            )}
            {expanded ? (
              <CaretUp size={18} className="text-white/60" />
            ) : (
              <CaretDown size={18} className="text-white/60" />
            )}
          </div>
        </div>
        
        {/* Hook Preview */}
        <p className="text-white font-medium mt-2 line-clamp-2">{post.hook}</p>

        {/* Round 67 — Collapsed-card readiness summary. Surfaces the
            EXACT blocking reason (e.g. "Video is still generating",
            "Caption is missing") on the collapsed card so the user
            doesn't have to expand every post to find out why it can't
            be approved. Hidden once the post is approved. */}
        {!post.approved && post.readiness && (() => {
          const r = post.readiness;
          if (r.is_ready_to_approve) {
            return (
              <p
                data-testid={`post-collapsed-status-${post.id}`}
                className="mt-2 text-xs text-green-300 flex items-center gap-1.5"
              >
                <CheckCircle size={12} weight="fill" />
                Ready to approve
              </p>
            );
          }
          const reasons = (r.blocked_reasons || []);
          if (!reasons.length) return null;
          return (
            <div
              data-testid={`post-collapsed-blocked-${post.id}`}
              onClick={(e) => e.stopPropagation()}
              className="mt-2 text-xs text-yellow-300/90 bg-yellow-500/5 border border-yellow-500/20 rounded-md px-2 py-1.5"
            >
              <span className="font-semibold">
                {r.approve_button_label || 'Not ready'}:
              </span>{' '}
              {reasons.join(' • ')}
            </div>
          );
        })()}
        {/* Round 78 — Generate/Preview Voice button MUST always render
            on the post card, whether expanded or collapsed. Previously
            it was tucked inside the {expanded && (...)} block so it
            "disappeared" after Edit/Save (which closes the modal but
            may leave the card collapsed). User spec: "If Weekly Planner
            says Voice = previewed and Video = missing, Generate Video
            must be visible. No exceptions."

            Round 80 — Approving a post (e.g. via "Approve All") used
            to hide Generate Video even when Video=missing / Public URL=
            missing. We now keep the workflow button visible whenever
            there is still video work to do, regardless of post.approved.
            Hide only when the workflow is fully complete: video ready
            on disk (next_action = VIDEO_READY) or post is approved AND
            no work is left (next_action = READY_TO_APPROVE). */}
        {(() => {
          const workflowState = getVideoNextAction({
            script: post.script,
            video_prompt: post.video_prompt,
            caption: post.caption,
            platform: post.platform,
            voice_preview: post.voice_preview,
            content_type: post.content_type,
            readiness: post.readiness,
          });
          const fullyDone =
            workflowState.next_action === NEXT_ACTION.VIDEO_READY ||
            (post.approved && workflowState.next_action === NEXT_ACTION.READY_TO_APPROVE);
          if (fullyDone) return null;
          return (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <VideoWorkflowActions
                asset={{
                  script: post.script,
                  video_prompt: post.video_prompt,
                  caption: post.caption,
                  platform: post.platform,
                  voice_preview: post.voice_preview,
                  content_type: post.content_type,
                  readiness: post.readiness,
                }}
                onPersistVoicePreview={async (voicePreview) => {
                  await api.put('/weekly-content/post', {
                    post_id: post.id,
                    voice_preview: voicePreview,
                  });
                  onPreviewVoice?.(post.id, voicePreview);
                }}
                onGenerateVideo={() => onGenerateVideo?.(post)}
                onRetryVideo={() => onRetryVideo?.(post)}
                testIdPrefix={`workflow-${post.id}`}
                isInternalAccount={isInternalAccount}
              />
              {/* Round 86 — Inline mini voiceover player. Renders on
                  the card whenever a voice_preview exists EITHER on
                  the post (server-side persisted) OR in the
                  voicePreviewCache prop (client-side stamped, survives
                  background loadCurrentContent overwrites). Mirrors
                  the audio-url base-stamping logic used by
                  VoiceoverPreviewPanel. UI-only fix. */}
              {(() => {
                const cached = voicePreviewCache?.[post.id];
                const serverVp = post.voice_preview;
                const effective = cached || serverVp || null;
                const audioUrlRaw = effective?.audio_url
                  || effective?.voice_preview_url
                  || effective?.voiceover_url
                  || effective?.audioUrl
                  || effective?.generated_audio_url
                  || null;
                const durRaw = effective?.audio_duration
                  || effective?.duration
                  || effective?.audioDuration
                  || null;
                const apiBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                const audioFullUrl = audioUrlRaw && typeof audioUrlRaw === 'string' && audioUrlRaw.startsWith('/')
                  ? `${apiBackend}${audioUrlRaw}`
                  : audioUrlRaw;
                return (
                  <>
                    {audioFullUrl && (
                      <div
                        className="mt-2 rounded-md border border-violet-500/20 bg-violet-500/5 px-2.5 py-2 flex items-center gap-2"
                        data-testid={`inline-voice-preview-wrapper-${post.id}`}
                      >
                        <Microphone size={14} className="text-violet-300 shrink-0" />
                        <audio
                          key={audioFullUrl}
                          controls
                          preload="auto"
                          src={audioFullUrl}
                          data-testid={`inline-voice-preview-player-${post.id}`}
                          className="flex-1 h-8"
                        >
                          Your browser does not support audio playback.
                        </audio>
                        {Number.isFinite(Number(durRaw)) && (
                          <span
                            className="text-[11px] text-emerald-400 whitespace-nowrap"
                            data-testid={`inline-voice-preview-duration-${post.id}`}
                          >
                            {Number(durRaw).toFixed(1)}s
                          </span>
                        )}
                      </div>
                    )}
                    {/* Round 86 — Founder-only debug line. Visible only
                        on internal/admin accounts. Confirms whether the
                        voice-preview URL is actually wired to this post
                        in the current render. Clients never see this. */}
                    {isInternalAccount && (
                      <p
                        className="mt-1 text-[10px] font-mono text-slate-500"
                        data-testid={`inline-voice-preview-debug-${post.id}`}
                      >
                        voice preview url: {audioFullUrl ? 'present' : 'missing'}
                        {' · '}duration: {Number.isFinite(Number(durRaw)) ? `${Number(durRaw).toFixed(1)}s` : '—'}
                        {' · '}source: {cached ? 'client-cache' : (serverVp ? 'server' : 'none')}
                      </p>
                    )}
                  </>
                );
              })()}
              {/* Phase 4.3 — Founder-only "Generate with v2" button.
                  Sits beside the legacy VideoWorkflowActions and uses
                  the new /weekly-content/post/{id}/generate-video-v2
                  endpoint. Clients NEVER see this — three independent
                  gates: isInternalAccount + onGenerateVideoV2 prop +
                  v2EnvAvailable. */}
              {isInternalAccount
                && typeof onGenerateVideoV2 === 'function'
                && (post.script || '').trim()
                && (post.video_prompt || '').trim() && (
                <div className="mt-2" data-testid={`run-my-week-v2-panel-${post.id}`}>
                  <button
                    type="button"
                    data-testid={`run-my-week-v2-btn-${post.id}`}
                    disabled={!v2EnvAvailable || v2JobPostId === post.id}
                    onClick={() => onGenerateVideoV2(post)}
                    className={`px-3 py-1.5 text-[11px] font-semibold rounded-md border whitespace-nowrap transition-all ${
                      !v2EnvAvailable
                        ? 'border-slate-700 bg-slate-800/60 text-slate-500 cursor-not-allowed'
                        : v2JobPostId === post.id
                          ? 'border-amber-500/40 bg-amber-500/15 text-amber-200 cursor-wait'
                          : 'border-amber-500/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25'
                    }`}
                    title={
                      !v2EnvAvailable
                        ? 'v2 disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend & restart.'
                        : v2JobPostId === post.id
                          ? 'v2 generation in progress…'
                          : 'Founder-only: rebuild this post via Video Engine v2.'
                    }
                  >
                    {v2JobPostId === post.id
                      ? 'v2: running…'
                      : (!v2EnvAvailable ? 'v2 disabled' : 'Founder · Generate with v2')}
                  </button>
                  {/* Phase 4.4 — Founder-only "Retry with v2" sibling.
                      Tombstones any failed legacy video_jobs rows for
                      this post before kicking off a fresh v2 attempt.
                      Surfaced ONLY when at least one legacy attempt has
                      already happened (post.video_status==='failed' or
                      'needs_repair' or post has a job_id). */}
                  {typeof onRetryVideoV2 === 'function'
                    && (post.video_status === 'failed'
                        || post.video_status === 'needs_repair'
                        || post.video_failure_reason) && (
                    <button
                      type="button"
                      data-testid={`run-my-week-v2-retry-btn-${post.id}`}
                      disabled={!v2EnvAvailable || v2JobPostId === post.id}
                      onClick={() => onRetryVideoV2(post)}
                      className={`ml-2 px-3 py-1.5 text-[11px] font-semibold rounded-md border whitespace-nowrap transition-all ${
                        !v2EnvAvailable
                          ? 'border-slate-700 bg-slate-800/60 text-slate-500 cursor-not-allowed'
                          : v2JobPostId === post.id
                            ? 'border-amber-500/40 bg-amber-500/15 text-amber-200 cursor-wait'
                            : 'border-amber-600/60 bg-amber-600/15 text-amber-100 hover:bg-amber-600/25'
                      }`}
                      title={
                        !v2EnvAvailable
                          ? 'v2 disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend & restart.'
                          : v2JobPostId === post.id
                            ? 'v2 retry in progress…'
                            : 'Founder-only: retire the failed legacy attempt and rebuild via v2.'
                      }
                    >
                      {v2JobPostId === post.id
                        ? 'v2: running…'
                        : (!v2EnvAvailable ? 'v2 disabled' : 'Founder · Retry with v2')}
                    </button>
                  )}
                  {!v2EnvAvailable && (
                    <p className="text-[10px] font-mono text-amber-300/70 mt-1" data-testid={`run-my-week-v2-disabled-${post.id}`}>
                      v2_env_enabled=false · set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on backend &amp; restart.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10"
          >
            <div className="p-4 space-y-4">
              {/* Hook (full) */}
              <div>
                <label className="text-xs font-semibold text-yellow-300 uppercase tracking-wider flex items-center gap-1">
                  <Lightning size={12} weight="fill" />
                  Hook
                </label>
                <p className="text-yellow-100 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mt-1 text-sm font-medium">
                  {post.hook}
                </p>
              </div>

              {/* Script */}
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Script ({post.script_word_count} words)</label>
                <p className="text-slate-300 mt-1 whitespace-pre-line text-sm">{post.script}</p>
              </div>

              {/* Video Prompt — parity with client "Let's Create Content" view */}
              <div data-testid={`video-prompt-section-${post.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-fuchsia-300 uppercase tracking-wider">Video Prompt</label>
                  {post.video_prompt && (
                    <button
                      onClick={handleCopyVideoPrompt}
                      data-testid={`copy-video-prompt-${post.id}`}
                      className="text-xs px-2 py-1 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 rounded flex items-center gap-1"
                    >
                      <Copy size={12} />
                      <span>Copy Video Prompt</span>
                    </button>
                  )}
                </div>
                {post.video_prompt ? (
                  <p className="text-slate-200 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded-lg p-3 text-sm whitespace-pre-line">
                    {post.video_prompt}
                  </p>
                ) : (
                  <div className="flex items-center justify-between gap-2 bg-slate-500/10 border border-slate-500/20 rounded-lg p-2 text-xs text-slate-400">
                    <div className="flex items-center gap-2 min-w-0">
                      <Warning size={12} className="flex-shrink-0" />
                      <span className="truncate">Video prompt missing.</span>
                    </div>
                    <button
                      onClick={handleRegenerateVideoPrompt}
                      disabled={regeneratingPrompt}
                      data-testid={`regenerate-video-prompt-${post.id}`}
                      className="flex-shrink-0 text-xs px-2 py-1 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 disabled:opacity-60 disabled:cursor-not-allowed text-fuchsia-200 rounded flex items-center gap-1"
                    >
                      {regeneratingPrompt ? (
                        <>
                          <Spinner size={12} className="animate-spin" />
                          <span>Generating…</span>
                        </>
                      ) : (
                        <>
                          <Sparkle size={12} weight="fill" />
                          <span>Regenerate video prompt</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Caption */}
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Caption</label>
                <p className="text-white font-medium mt-1">{post.caption}</p>
              </div>

              {/* Round 71 — Voiceover Preview (matches Create Content Bot) */}
              <VoiceoverPreviewPanel post={post} onPreviewed={onPreviewVoice} />

              {/* Round 55 — Finished asset preview + readiness state */}
              <PostReadinessSection post={post} />

              {/* Hashtags */}
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Hashtags</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {post.hashtags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-white/10 rounded text-blue-300 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions — Round 67: mobile-friendly. Stacks vertically on
                  small screens so the Approve button is always reachable
                  without horizontal scrolling. Approve takes full width when
                  it's the primary intent (post not yet approved). */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    data-testid={`copy-post-${post.id}`}
                    className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-colors"
                  >
                    {copied ? <CheckFat size={14} weight="fill" className="text-green-400" /> : <Copy size={14} />}
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </button>

                  <button
                    onClick={() => onEdit?.(post)}
                    data-testid={`edit-post-${post.id}`}
                    className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-colors"
                  >
                    <PencilSimple size={14} />
                    <span>Edit</span>
                  </button>
                </div>

                {/* Round 78 — Generate/Preview Voice button moved to
                    card header (always visible). Approve button stays
                    here inside the expanded block since approval is a
                    "final step" review action. */}

                {!post.approved && (() => {
                  const r = post.readiness || {};
                  const ready = r.is_ready_to_approve === true;
                  const label = r.approve_button_label || (ready ? 'Approve' : 'Not ready to approve');
                  // Hide the Approve pill while we're showing the
                  // Generate/Retry Video CTA — Approve is meaningless
                  // until the video exists.
                  const videoBlocking =
                    r.video_status === 'missing' ||
                    r.video_status === 'generating' ||
                    r.video_status === 'failed';
                  if (videoBlocking && !ready) return null;
                  return (
                    <button
                      onClick={() => ready && onApprove?.(post)}
                      disabled={!ready}
                      data-testid={`approve-post-${post.id}`}
                      title={ready ? 'Approve this finished post' : (r.blocked_reasons || []).join(' • ')}
                      className={`w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        ready
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-slate-700/50 text-slate-400 cursor-not-allowed border border-slate-600/50'
                      }`}
                    >
                      <CheckCircle size={16} weight="fill" />
                      <span>{label}</span>
                    </button>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Day Section Component
const DaySection = ({ day, posts, onApprove, onEdit, onCopy, onGenerateVideo, onRetryVideo, onPreviewVoice, expandedPosts, onToggleExpand, onVideoPromptRegenerated, isInternalAccount = false, v2EnvAvailable = false, onGenerateVideoV2 = null, onRetryVideoV2 = null, v2JobPostId = null, voicePreviewCache = {} }) => {
  const theme = DAY_THEMES[day] || { name: 'Content', color: 'slate', icon: '📝' };
  
  return (
    <div className="space-y-3" data-testid={`day-section-${day.toLowerCase()}`}>
      {/* Day Header */}
      <div className="flex items-center space-x-3 sticky top-0 bg-slate-900/95 backdrop-blur py-2 z-10">
        <span className="text-2xl">{theme.icon}</span>
        <div>
          <h3 className="text-white font-bold text-lg">{day}</h3>
          <p className="text-slate-400 text-sm">Theme: {theme.name}</p>
        </div>
        <span className="ml-auto text-slate-500 text-sm">{posts.length} posts</span>
      </div>

      {/* Posts for this day */}
      <div className="space-y-3">
        {posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onApprove={onApprove}
            onEdit={onEdit}
            onCopy={onCopy}
            onGenerateVideo={onGenerateVideo}
            onRetryVideo={onRetryVideo}
            onPreviewVoice={onPreviewVoice}
            expanded={expandedPosts.includes(post.id)}
            onToggleExpand={onToggleExpand}
            onVideoPromptRegenerated={onVideoPromptRegenerated}
            isInternalAccount={isInternalAccount}
            v2EnvAvailable={v2EnvAvailable}
            onGenerateVideoV2={onGenerateVideoV2}
            onRetryVideoV2={onRetryVideoV2}
            v2JobPostId={v2JobPostId}
            voicePreviewCache={voicePreviewCache}
          />
        ))}
      </div>
    </div>
  );
};

// Main Component
const WeeklyContentPlanner = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [contentPlan, setContentPlan] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState([]);
  const [filterAccount, setFilterAccount] = useState('all');
  const [editingPost, setEditingPost] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // {current: 0, total: 0, status: 'idle'}

  // Phase 4.3 — founder-only Run My Week V2 wiring.
  // `v2EnvAvailable=true` only when the backend reports
  // `VIDEO_ENGINE_V2_REAL_PROVIDERS=1` AND both real-provider keys are
  // configured. Drives whether the founder sees the "Generate with v2"
  // button at all. Re-fetched whenever the modal opens (because the
  // founder might flip the env flag mid-session for canary testing).
  const [isInternalAccount, setIsInternalAccount] = useState(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const u = JSON.parse(raw);
      return !!(u?.is_internal || u?.plan_tier === 'internal' || u?.role === 'admin');
    } catch (_e) { return false; }
  });
  const [v2EnvAvailable, setV2EnvAvailable] = useState(false);
  const [v2JobPostId, setV2JobPostId] = useState(null);
  // Round 86 — Client-side voice-preview cache. Survives the
  // background `loadCurrentContent()` overwrite that runs right
  // after a successful preview, so the inline mini-player keeps
  // rendering on the card. Indexed by post.id → voicePreview obj.
  // UI-only (does NOT touch the server-side post.voice_preview).
  const [voicePreviewCache, setVoicePreviewCache] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadCurrentContent();
      // Pull v2 status whenever the planner opens — founders may
      // arm/disarm the env flag between sessions.
      (async () => {
        try {
          const r = await api.get('/v2-demo/status');
          setV2EnvAvailable(!!r?.data?.real_providers_available);
          setIsInternalAccount(true); // /status only returns 200 for internal
        } catch (_e) {
          setV2EnvAvailable(false);
          // Don't flip isInternal — preserve cached state.
        }
      })();
    }
  }, [isOpen]);

  const loadCurrentContent = async () => {
    setLoading(true);
    try {
      const response = await api.get('/weekly-content/current');
      if (response.data?.success && response.data?.data) {
        setContentPlan(response.data.data);
      }
    } catch (error) {
      console.error('Load content error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateContent = async (regenerate = false) => {
    setGenerating(true);
    try {
      const response = await api.post('/weekly-content/generate', { regenerate });
      if (response.data?.success && response.data?.data) {
        setContentPlan(response.data.data);
        toast.success(`Generated ${response.data.data.total_posts} posts!`);
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  // Round 68 — Generate Video for a single post. Hits the new
  // /weekly-content/post/{id}/generate-video endpoint which reuses
  // the SAME pipeline /api/videos/create uses (VideoJobService → DFY
  // worker → voice-first sizing → iOS-normalize). The button caller
  // updates local state to show the "Generating video…" disabled
  // state immediately; the next planner refresh will pick up the
  // real readiness state from the backend.
  const handleGenerateVideo = async (post) => {
    // Optimistically mark this post as generating so the button
    // disables instantly without waiting for the API round-trip.
    setContentPlan((prev) => ({
      ...prev,
      posts: (prev?.posts || []).map((p) =>
        p.id === post.id
          ? {
              ...p,
              readiness: {
                ...(p.readiness || {}),
                video_status: 'generating',
                approve_button_label: 'Video still generating',
                is_ready_to_approve: false,
                blocked_reasons: ['Video is still generating.'],
              },
            }
          : p,
      ),
    }));
    try {
      // Phase D (2026-05-27) — Weekly Planner Generate now routes to
      // v2 for ALL users. Legacy `/generate-video` is permanently
      // 503'd; routing here through v2 means clients (with billing
      // gate) and founders share one code path. If a 402 fires
      // (extended-tier upcharge), we surface it with the backend's
      // structured error payload so the UI can prompt the user.
      const res = await api.post(
        `/weekly-content/post/${post.id}/generate-video-v2`,
      );
      if (res?.data?.already_inflight) {
        toast.info('Video is already being generated for this post.');
      } else {
        toast.success('Video generation started.');
      }
      // Refresh once so we pick up the real backend readiness payload.
      await loadCurrentContent();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      const msg =
        typeof detail === 'string'
          ? detail
          : (detail && detail.message) || error?.message || 'Failed to start video generation';
      toast.error(msg);
      // Roll back the optimistic state so the button comes back.
      await loadCurrentContent();
      if (status === 402) {
        // Phase D — surface upcharge_confirmation_required to the user.
        const upchargeUsd =
          (detail && (detail.upcharge_per_video_usd || detail.estimate?.upcharge_per_video_usd)) ||
          2;
        toast.error(
          `Extended-length script — $${upchargeUsd} upcharge required. ` +
          'Shorten the script or confirm the upcharge to proceed.',
          { duration: 8000 },
        );
      }
    }
  };

  // Phase 4.3 — Founder-only "Generate with v2" handler. Posts to the
  // new `/api/weekly-content/post/{post_id}/generate-video-v2`
  // endpoint, then polls `/api/videos/v2-status/{job_id}` until done.
  // On success a NEW generated_content row is created tagged
  // engine="v2". The planner post row is NEVER mutated by v2.
  //
  // Bails out early with a clear toast if the backend env flag is
  // OFF instead of submitting and getting a 409 mid-flow.
  const handleGenerateVideoV2 = async (post) => {
    if (!isInternalAccount) return;  // belt-and-braces; UI hides this for clients
    if (!v2EnvAvailable) {
      toast.error(
        'v2 disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
      );
      return;
    }
    if (v2JobPostId) {
      toast.error('A v2 generation is already running.');
      return;
    }
    setV2JobPostId(post.id);
    try {
      const resp = await api.post(
        `/weekly-content/post/${post.id}/generate-video-v2`,
        {}
      );
      const jobId = resp?.data?.job_id;
      if (!jobId) throw new Error('v2 generation returned no job_id');
      toast.success('v2 generation queued — ElevenLabs + Runway running.');

      const pollInterval = setInterval(async () => {
        try {
          const s = await api.get(`/videos/v2-status/${jobId}`);
          const status = s?.data?.status;
          if (status === 'completed') {
            clearInterval(pollInterval);
            setV2JobPostId(null);
            toast.success(
              'v2 generation complete — new voiced video saved to My Projects.'
            );
            await loadCurrentContent();
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setV2JobPostId(null);
            const code = s?.data?.error_code || 'engine_failed';
            const msg = s?.data?.error_message || 'v2 generation failed.';
            toast.error(`v2 failed (${code}): ${msg}`, { duration: 8000 });
          }
        } catch (e) {
          console.warn('[RunMyWeekV2] status poll error', e);
        }
      }, 5000);

      // Hard 5-minute timeout — same ceiling as Phase 4.2 retry.
      setTimeout(() => {
        clearInterval(pollInterval);
        if (v2JobPostId === post.id) {
          setV2JobPostId(null);
          toast.error('v2 generation timed out after 5 min — check backend logs.');
        }
      }, 300000);
    } catch (e) {
      setV2JobPostId(null);
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      toast.error(`v2 generation could not start: ${detail}`);
    }
  };


  // Round 71/74 — When VoiceoverPreviewPanel successfully previews,
  // stamp the preview onto local post state immediately AND patch the
  // readiness object so the "Voice" badge flips from "missing" →
  // "previewed" without waiting for a re-fetch. We still kick off a
  // background loadContent() to pick up the canonical server-computed
  // readiness (and any other side-effects).
  // Phase 4.4 — Founder-only "Retry with v2" handler for Weekly
  // Planner. Sibling of `handleGenerateVideoV2` (4.3) but routes to
  // the `retry-video-v2` endpoint which additionally tombstones any
  // failed legacy `video_jobs` rows for the post (so the next
  // legacy worker tick stops seeing them).
  const handleRetryVideoV2 = async (post) => {
    if (!isInternalAccount) return;
    if (!v2EnvAvailable) {
      toast.error(
        'v2 disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
      );
      return;
    }
    if (v2JobPostId) {
      toast.error('A v2 generation is already running.');
      return;
    }
    setV2JobPostId(post.id);
    try {
      const resp = await api.post(
        `/weekly-content/post/${post.id}/retry-video-v2`,
        {}
      );
      const jobId = resp?.data?.job_id;
      if (!jobId) throw new Error('v2 retry returned no job_id');
      const tombstoned = resp?.data?.tombstoned_legacy_count || 0;
      toast.success(
        `v2 retry queued${tombstoned ? ` (${tombstoned} old attempt${tombstoned === 1 ? '' : 's'} retired)` : ''} — running.`
      );

      const pollInterval = setInterval(async () => {
        try {
          const s = await api.get(`/videos/v2-status/${jobId}`);
          const status = s?.data?.status;
          if (status === 'completed') {
            clearInterval(pollInterval);
            setV2JobPostId(null);
            toast.success('v2 retry complete — new voiced video saved to My Projects.');
            await loadCurrentContent();
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setV2JobPostId(null);
            const code = s?.data?.error_code || 'engine_failed';
            const msg = s?.data?.error_message || 'v2 retry failed.';
            toast.error(`v2 retry failed (${code}): ${msg}`, { duration: 8000 });
          }
        } catch (e) {
          console.warn('[RetryVideoV2] status poll error', e);
        }
      }, 5000);
      setTimeout(() => {
        clearInterval(pollInterval);
        if (v2JobPostId === post.id) {
          setV2JobPostId(null);
          toast.error('v2 retry timed out after 5 min — check backend logs.');
        }
      }, 300000);
    } catch (e) {
      setV2JobPostId(null);
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      const detailText = typeof detail === 'string' ? detail : JSON.stringify(detail);
      toast.error(`v2 retry could not start: ${detailText}`);
    }
  };

  const handlePreviewVoice = (postId, voicePreview) => {
    // Round 86 — Stamp into the client cache FIRST so any
    // subsequent loadCurrentContent() that doesn't return
    // voice_preview (e.g. when the persistence wrote to a
    // sibling plan) cannot wipe the inline player.
    if (voicePreview?.audio_url) {
      setVoicePreviewCache((prev) => ({ ...prev, [postId]: voicePreview }));
    }
    setContentPlan((prev) => ({
      ...prev,
      posts: (prev?.posts || []).map((p) =>
        p.id === postId
          ? {
              ...p,
              voice_preview: voicePreview,
              readiness: {
                ...(p.readiness || {}),
                // Mirror what services/approval_readiness.py computes
                // when post.voice_preview exists. Removes the stale
                // "Voiceover has not been attached." reason so the
                // badge + blocked-reasons list update in lock-step.
                audio_status: 'previewed',
                blocked_reasons: ((p.readiness && p.readiness.blocked_reasons) || []).filter(
                  (reason) => !/voiceover.*attached/i.test(reason || ''),
                ),
              },
            }
          : p,
      ),
    }));
    // Belt-and-braces: refresh from server in the background so server
    // truth wins on the next render. Fire-and-forget; we already
    // patched local state for an instant UI response.
    (async () => {
      try {
        await loadCurrentContent();
      } catch (refreshErr) {
        console.warn('[WeeklyPlanner] post-voice-preview refresh failed', refreshErr);
      }
    })();
  };

  // Round 70 — Retry Video after a previous job failed. Hits the new
  // /weekly-content/post/{id}/retry-video endpoint which tombstones
  // the failed row and re-enqueues a fresh one via the same pipeline
  // /generate-video uses.
  const handleRetryVideo = async (post) => {
    setContentPlan((prev) => ({
      ...prev,
      posts: (prev?.posts || []).map((p) =>
        p.id === post.id
          ? {
              ...p,
              readiness: {
                ...(p.readiness || {}),
                video_status: 'generating',
                approve_button_label: 'Video still generating',
                is_ready_to_approve: false,
                blocked_reasons: ['Video is still generating.'],
              },
            }
          : p,
      ),
    }));
    try {
      // Phase D (2026-05-27) — Weekly Planner Retry routes to v2 for
      // all users. Legacy `/retry-video` is permanently 503'd.
      await api.post(`/weekly-content/post/${post.id}/retry-video-v2`);
      toast.success('Retry started. The video is being generated again.');
      await loadCurrentContent();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      const msg =
        typeof detail === 'string'
          ? detail
          : (detail && detail.message) || error?.message || 'Failed to retry video generation';
      toast.error(msg);
      await loadCurrentContent();
      if (status === 402) {
        const upchargeUsd =
          (detail && (detail.upcharge_per_video_usd || detail.estimate?.upcharge_per_video_usd)) ||
          2;
        toast.error(
          `Extended-length script — $${upchargeUsd} upcharge required. ` +
          'Shorten the script or confirm the upcharge to proceed.',
          { duration: 8000 },
        );
      }
    }
  };

  const handleApprove = async (post, generateVideo = false, opts = {}) => {
    // Round 55 — block client-side approval when readiness says not ready.
    // Phase F (2026-05-27): when generateVideo=true the "Video has not been
    // generated yet" blocker is EXPECTED — we're about to create the
    // video — so skip the local-readiness check for that case. The
    // batch-approve-and-generate-v2 endpoint applies its own relaxed
    // readiness rule server-side.
    const r = post.readiness;
    if (!generateVideo && r && r.is_ready_to_approve === false) {
      const reasons = (r.blocked_reasons || []).join(' • ') || 'Post is not ready to approve.';
      toast.error(`Cannot approve: ${reasons}`);
      return;
    }

    // Phase F — approve-and-generate uses the batch v2 endpoint with a
    // single post_id. This funnels the single-post and "Approve All +
    // Videos" flows through the same server-side validator (no partial
    // spending, billing gate before any provider call). The legacy
    // approve-and-generate endpoint is only used for the pure
    // approve-only path because its readiness gate requires the video
    // to already exist.
    if (generateVideo) {
      try {
        const response = await api.post('/weekly-content/batch-approve-and-generate-v2', {
          post_ids: [post.id],
          upcharge_confirmed: Boolean(opts.upcharge_confirmed),
        });
        setContentPlan(prev => ({
          ...prev,
          posts: prev.posts.map(p =>
            p.id === post.id ? {
              ...p,
              approved: true,
              status: 'approved',
              generation_status: 'generating',
            } : p
          ),
        }));
        toast.success('Post approved & video generation started!');
        return response;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const status = error?.response?.status;
        if (status === 402 && detail?.error === 'upcharge_confirmation_required') {
          const usd =
            detail.upcharge_per_video_usd ??
            detail?.estimate?.upcharge_per_video_usd;
          const confirmMsg =
            (detail.message || 'This post is an extended-length video.') +
            (usd ? ` (+$${Number(usd).toFixed(2)} per extended video).` : '') +
            ' Approve the upcharge to start generation?';
          if (window.confirm(confirmMsg)) {
            return handleApprove(post, true, { upcharge_confirmed: true });
          }
          toast.warning('Video not generated — upcharge declined.');
          return;
        }
        if (status === 400 && detail?.error === 'script_too_long') {
          toast.error('Script is too long for v2 video. Shorten it and retry.');
          return;
        }
        if (status === 409 && detail?.error === 'post_not_ready_to_approve') {
          const reasons = (detail.blocked_reasons || []).join(' • ') || 'post not ready';
          toast.error(`Cannot approve: ${reasons}`);
          return;
        }
        const fallback =
          typeof detail === 'string'
            ? detail
            : detail?.message || detail?.error || 'Approve-and-generate failed.';
        toast.error(`Approve-and-generate failed: ${fallback}`);
        return;
      }
    }

    // Approve-only path — keep the legacy endpoint with generate_video=false.
    try {
      const response = await api.post('/weekly-content/approve-and-generate', {
        post_id: post.id,
        generate_video: false,
      });
      setContentPlan(prev => ({
        ...prev,
        posts: prev.posts.map(p =>
          p.id === post.id ? {
            ...p,
            approved: true,
            status: 'approved',
            generation_status: null,
          } : p
        )
      }));
      toast.success('Post approved & saved to My Creations!');
      return response;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail) {
        const reasons = (detail.blocked_reasons || []).join(' • ')
          || detail.error
          || 'Post not ready to approve';
        toast.error(`Cannot approve: ${reasons}`);
        return;
      }
      toast.error('Failed to approve post');
    }
  };

  const handleApproveAll = async (generateVideos = false, opts = {}) => {
    if (!contentPlan) return;

    // Defensive: when called as a React onClick handler, the first arg is a
    // SyntheticEvent (cyclic). Coerce to a strict boolean so we never put a
    // DOM event into the request body (caused "cannot serialize cyclic
    // structures" on prod).
    const wantsVideos = generateVideos === true;

    const unapprovedPosts = contentPlan.posts.filter(p => !p.approved);
    if (unapprovedPosts.length === 0) {
      toast.info('All posts already approved');
      return;
    }

    const unapprovedIds = unapprovedPosts.map(p => String(p.id));
    setBatchProgress({ current: 0, total: unapprovedIds.length, status: 'approving' });

    // Phase F (2026-05-27) — the "Approve All + Videos" path routes
    // through the v2-only batch-approve-and-generate-v2 endpoint
    // exclusively. The legacy `/batch-approve` endpoint is kept only
    // for the pure-approval path (wantsVideos=false).
    if (wantsVideos) {
      try {
        const response = await api.post('/weekly-content/batch-approve-and-generate-v2', {
          post_ids: unapprovedIds,
          upcharge_confirmed: Boolean(opts.upcharge_confirmed),
        });
        const data = response.data || {};
        const approved = data.approved_count ?? 0;
        setContentPlan(prev => ({
          ...prev,
          status: approved === unapprovedIds.length ? 'approved' : prev.status,
          posts: prev.posts.map(p => {
            if (p.approved) return p;
            if (!unapprovedIds.includes(p.id)) return p;
            return { ...p, approved: true, status: 'approved', generation_status: 'generating' };
          }),
        }));
        toast.success(`Approved ${approved} posts & queued v2 video jobs!`);
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const status = error?.response?.status;

        // Per-item upcharge confirmation prompt — the v2 batch
        // endpoint refuses 402 if ANY post in the batch is
        // extended-tier and `upcharge_confirmed` is false. We surface
        // the per-post context and re-submit with confirmation if the
        // user accepts.
        if (
          status === 402 &&
          detail?.error === 'upcharge_confirmation_required'
        ) {
          const usd =
            detail.upcharge_per_video_usd ??
            detail?.estimate?.upcharge_per_video_usd;
          const which =
            typeof detail.item_index === 'number'
              ? ` (post ${detail.item_index + 1}/${unapprovedIds.length}${detail.post_id ? `: ${detail.post_id}` : ''})`
              : '';
          const confirmMsg =
            `One or more posts in this batch are extended-length videos${which}.` +
            (usd ? ` Upcharge: +$${Number(usd).toFixed(2)} per extended video.` : '') +
            ' Approve the upcharge for the whole batch?';
          if (window.confirm(confirmMsg)) {
            setBatchProgress(null);
            return handleApproveAll(true, { upcharge_confirmed: true });
          }
          toast.warning('Batch not started — extended-video upcharge declined.');
          return;
        }

        if (
          status === 409 &&
          detail?.error === 'post_not_ready_to_approve'
        ) {
          const reasons = (detail.blocked_reasons || []).join(' • ') || 'post not ready';
          toast.error(
            `Cannot approve all: post ${detail.item_index + 1}/${unapprovedIds.length} not ready — ${reasons}`
          );
          return;
        }

        const apiDetail =
          (typeof detail === 'string' && detail) ||
          (typeof detail?.message === 'string' && detail.message) ||
          (typeof detail?.error === 'string' && detail.error) ||
          (typeof error?.message === 'string' && error.message) ||
          'Approve All + Videos failed';
        // eslint-disable-next-line no-console
        console.warn('[WeeklyContent] batch-approve-and-generate-v2 failed', { status, detail: apiDetail });
        toast.error(`Approve All + Videos failed: ${apiDetail}`);
      } finally {
        setBatchProgress(null);
      }
      return;
    }

    try {
      const response = await api.post('/weekly-content/batch-approve', {
        post_ids: unapprovedIds,
        generate_videos: false,
      });

      const result = response.data?.data || {};
      const approvedCount = result.approved ?? 0;
      const failedCount = result.failed ?? 0;
      const errors = Array.isArray(result.errors) ? result.errors : [];

      // Posts that failed — don't mark them as approved locally.
      const failedIdSet = new Set(errors.map(e => e?.post_id).filter(Boolean));
      const allFailedMissing = failedCount > 0 && failedIdSet.size === 0;

      // Optimistically flip approved=true for every id we asked about EXCEPT the
      // ones the backend explicitly reported as failed.
      setContentPlan(prev => ({
        ...prev,
        status: failedCount === 0 ? 'approved' : prev.status,
        posts: prev.posts.map(p => {
          if (p.approved) return p;
          if (!unapprovedIds.includes(p.id)) return p;
          if (failedIdSet.has(p.id)) return p; // keep as not approved
          return { ...p, approved: true, status: 'approved' };
        })
      }));

      if (failedCount === 0 && approvedCount > 0) {
        toast.success(`Approved ${approvedCount} posts & saved to My Projects!`);
      } else if (approvedCount > 0 && failedCount > 0) {
        const firstErr = errors[0];
        const detail = firstErr?.error || 'unknown reason';
        toast.warning(
          `${approvedCount} approved, ${failedCount} failed${firstErr?.post_id ? ` (e.g. ${firstErr.post_id}: ${detail})` : `: ${detail}`}`
        );
      } else if (allFailedMissing) {
        toast.error(`Could not approve ${failedCount} posts — please retry.`);
      } else if (failedCount > 0) {
        const firstErr = errors[0];
        toast.error(
          `Failed to approve ${failedCount} posts${firstErr ? ` — ${firstErr.post_id}: ${firstErr.error}` : ''}`
        );
      } else {
        toast.info('No posts needed approval.');
      }
    } catch (error) {
      // Extract safe strings only — never log the raw error (cyclic).
      const apiDetail =
        (typeof error?.response?.data?.detail === 'string' && error.response.data.detail) ||
        (typeof error?.response?.data?.message === 'string' && error.response.data.message) ||
        (typeof error?.message === 'string' && error.message) ||
        'Request failed';
      const status = error?.response?.status;
      // Use console.warn with a safe message — avoid console.error(error) which
      // some loggers JSON.stringify and choke on cyclic axios objects.
      // eslint-disable-next-line no-console
      console.warn('[WeeklyContent] batch-approve failed', { status, detail: apiDetail });
      toast.error(`Approve All failed: ${apiDetail}`);
    } finally {
      setBatchProgress(null);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
  };

  const handleSaveEdit = async (editedPost) => {
    try {
      // Round 76 — Detect a script change. The shared workflow rule
      // says voice_preview becomes stale when the script changes
      // (its audio_duration belongs to the previous script). Clear
      // it server-side so next_action flips back to needs_voice_preview
      // on the next render.
      const original = (contentPlan?.posts || []).find((p) => p.id === editedPost.id);
      const scriptChanged =
        original && (original.script || '').trim() !== (editedPost.script || '').trim();
      const payload = {
        post_id: editedPost.id,
        hook: editedPost.hook,
        script: editedPost.script,
        caption: editedPost.caption,
        hashtags: editedPost.hashtags,
        ...(editedPost.video_prompt !== undefined && { video_prompt: editedPost.video_prompt }),
        ...(scriptChanged && { voice_preview: null }),
      };

      const res = await api.put('/weekly-content/post', payload);

      // Round 72 — Use the canonical post object the server returned so
      // we never drift from the source of truth (preserves post.id and
      // picks up server-side normalization). Fall back to the locally
      // edited object only if the server didn't return one.
      const serverPost = res?.data?.post || null;
      setContentPlan((prev) => ({
        ...prev,
        posts: (prev?.posts || []).map((p) =>
          p.id === editedPost.id
            ? { ...p, ...(serverPost || editedPost), edited: true }
            : p,
        ),
      }));

      setEditingPost(null);
      toast.success('Post updated!');
      // Belt-and-braces — refresh from server so the readiness payload
      // and any side-effects (queue rows, etc.) are picked up. Awaiting
      // here is fine; the modal is already closed.
      try {
        await loadCurrentContent();
      } catch (refreshErr) {
        console.warn('[WeeklyPlanner] post-save refresh failed', refreshErr);
      }
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const toggleExpand = (postId) => {
    setExpandedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  // Per-post video prompt update — keeps the card display in sync with the
  // server without re-fetching the whole weekly plan.
  const handleVideoPromptRegenerated = (postId, videoPrompt, videoPromptAlt) => {
    setContentPlan(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        posts: prev.posts.map(p =>
          p.id === postId
            ? { ...p, video_prompt: videoPrompt, video_prompt_alt: videoPromptAlt }
            : p
        ),
      };
    });
  };

  const expandAll = () => {
    if (!contentPlan) return;
    setExpandedPosts(contentPlan.posts.map(p => p.id));
  };

  const collapseAll = () => {
    setExpandedPosts([]);
  };

  // Group posts by day
  const postsByDay = contentPlan?.posts?.reduce((acc, post) => {
    if (!acc[post.day]) acc[post.day] = [];
    
    // Apply account filter
    if (filterAccount === 'all' || post.account === filterAccount) {
      acc[post.day].push(post);
    }
    
    return acc;
  }, {}) || {};

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="weekly-content-planner"
      >
        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Calendar size={22} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-white font-bold text-xl">Weekly Content Planner</h2>
                <p className="text-slate-400 text-sm">21 posts for Founder + PayGBot</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="close-planner-btn"
            >
              <X size={24} />
            </button>
          </div>

          {/* Filters & Actions */}
          {contentPlan && (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {/* Account Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-slate-400 text-sm">Filter:</span>
                <select
                  value={filterAccount}
                  onChange={(e) => setFilterAccount(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm"
                  data-testid="filter-account-select"
                >
                  <option value="all">All Accounts</option>
                  <option value="founder">Founder Only</option>
                  <option value="paygbot">PayGBot Only</option>
                </select>
              </div>

              {/* Expand/Collapse */}
              <button
                onClick={expandAll}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white"
              >
                Collapse All
              </button>

              {/* Approve All */}
              <button
                onClick={() => handleApproveAll(false)}
                data-testid="approve-all-btn"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-sm text-green-300"
              >
                <ListChecks size={16} />
                <span>Approve All</span>
              </button>

              {/* Phase F (2026-05-27) — Approve All + Videos via v2 only */}
              <button
                onClick={() => handleApproveAll(true)}
                data-testid="approve-all-with-videos-btn"
                title="Approve every unapproved post AND queue v2 video jobs (no partial spending)"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg text-sm text-emerald-300"
              >
                <ListChecks size={16} />
                <span>Approve All + Videos</span>
              </button>

              {/* Regenerate */}
              <button
                onClick={() => generateContent(true)}
                disabled={generating}
                data-testid="regenerate-btn"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-sm text-purple-300"
              >
                <ArrowsClockwise size={16} className={generating ? 'animate-spin' : ''} />
                <span>Regenerate</span>
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : !contentPlan ? (
            // No content - Generate prompt
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Sparkle size={32} className="text-purple-400" weight="fill" />
              </div>
              <div className="text-center">
                <h3 className="text-white font-semibold text-lg">No Content Plan Yet</h3>
                <p className="text-slate-400 text-sm mt-1">Generate weekly content for your soft launch</p>
              </div>
              <button
                onClick={() => generateContent(false)}
                disabled={generating}
                data-testid="generate-content-btn"
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-all"
              >
                {generating ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Lightning size={20} weight="fill" />
                    <span>Generate 21 Posts</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            // Content Plan Display
            <div className="space-y-8">
              {/* Validation Status */}
              {contentPlan.validation && (
                <div className={`p-4 rounded-xl border ${
                  contentPlan.validation.status === 'PASS' 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center space-x-2">
                    {contentPlan.validation.status === 'PASS' ? (
                      <CheckCircle size={20} className="text-green-400" weight="fill" />
                    ) : (
                      <Warning size={20} className="text-red-400" weight="fill" />
                    )}
                    <span className={contentPlan.validation.status === 'PASS' ? 'text-green-400' : 'text-red-400'}>
                      Validation: {contentPlan.validation.status}
                    </span>
                    <span className="text-slate-400 text-sm">
                      | {contentPlan.validation.total_posts} posts ({contentPlan.validation.founder_posts} Founder, {contentPlan.validation.paygbot_posts} PayGBot)
                    </span>
                  </div>
                </div>
              )}

              {/* Week Info */}
              <div className="flex items-center space-x-4 text-sm text-slate-400">
                <span>Week: {new Date(contentPlan.week_start).toLocaleDateString()} - {new Date(contentPlan.week_end).toLocaleDateString()}</span>
                <span>|</span>
                <span>Status: <span className={contentPlan.status === 'approved' ? 'text-green-400' : 'text-yellow-400'}>{contentPlan.status}</span></span>
              </div>

              {/* Days */}
              {(() => {
                // Round 78 — Posts with missing/non-canonical day were
                // silently filtered out, hiding the entire planner. Add
                // an "Unscheduled" bucket so EVERY post renders (and
                // its always-visible Generate Video button is reachable).
                const sectionsToRender = [...dayOrder];
                const extraDays = Object.keys(postsByDay).filter((d) => !dayOrder.includes(d));
                if (extraDays.length) sectionsToRender.push(...extraDays);
                // Posts with null/undefined day end up at key 'null' or 'undefined' in the reduce.
                const renderable = sectionsToRender.filter((d) => (postsByDay[d] || []).length > 0);
                if (renderable.length === 0 && (contentPlan?.posts || []).length > 0) {
                  // Fallback — show ALL posts in one bucket so they're never hidden.
                  renderable.push('Unscheduled');
                  postsByDay['Unscheduled'] = contentPlan.posts;
                }
                return renderable.map((day) => (
                  <DaySection
                    key={day}
                    day={day}
                    posts={postsByDay[day] || []}
                    onApprove={handleApprove}
                    onEdit={handleEdit}
                    onCopy={() => {}}
                    onGenerateVideo={handleGenerateVideo}
                    onRetryVideo={handleRetryVideo}
                    onPreviewVoice={handlePreviewVoice}
                    expandedPosts={expandedPosts}
                    onToggleExpand={toggleExpand}
                    onVideoPromptRegenerated={handleVideoPromptRegenerated}
                    isInternalAccount={isInternalAccount}
                    v2EnvAvailable={v2EnvAvailable}
                    onGenerateVideoV2={handleGenerateVideoV2}
                    onRetryVideoV2={handleRetryVideoV2}
                    v2JobPostId={v2JobPostId}
                    voicePreviewCache={voicePreviewCache}
                  />
                ));
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        {contentPlan && (
          <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-white/10 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-slate-400 text-sm">
                {contentPlan.posts?.filter(p => p.approved).length || 0} / {contentPlan.total_posts} approved
              </div>
              <div className="text-xs text-slate-500">
                Ready for Metricool scheduling • No auto-posting
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Edit Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          onSave={handleSaveEdit}
          onClose={() => setEditingPost(null)}
        />
      )}
    </div>
  );
};

// Edit Post Modal
const EditPostModal = ({ post, onSave, onClose }) => {
  const [editedPost, setEditedPost] = useState({ ...post });

  const handleChange = (field, value) => {
    setEditedPost(prev => ({ ...prev, [field]: value }));
  };

  const handleHashtagChange = (value) => {
    const hashtags = value.split(' ').filter(h => h.startsWith('#') || h.trim());
    setEditedPost(prev => ({ ...prev, hashtags }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4"
        data-testid="edit-post-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">Edit Post</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400">Hook</label>
            <input
              type="text"
              value={editedPost.hook}
              onChange={(e) => handleChange('hook', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white mt-1"
              data-testid="edit-hook-input"
            />
          </div>

          <div>
            <label className="text-sm text-slate-400">Script</label>
            <textarea
              value={editedPost.script}
              onChange={(e) => handleChange('script', e.target.value)}
              rows={5}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white mt-1"
              data-testid="edit-script-input"
            />
          </div>

          {/* Round 67 — video_prompt edit. Surfaced under Script so the
              user can tune the visuals without leaving the modal. The
              text area is intentionally taller; Runway prompts are
              usually 2-5 sentences. */}
          <div>
            <label className="text-sm text-slate-400">Video Prompt</label>
            <textarea
              value={editedPost.video_prompt || ''}
              onChange={(e) => handleChange('video_prompt', e.target.value)}
              rows={4}
              placeholder="Describe the visuals you want Runway to generate…"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white mt-1"
              data-testid="edit-video-prompt-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              This is what Runway sees. Edits trigger a fresh video on the next generation pass.
            </p>
          </div>

          <div>
            <label className="text-sm text-slate-400">Caption</label>
            <input
              type="text"
              value={editedPost.caption}
              onChange={(e) => handleChange('caption', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white mt-1"
              data-testid="edit-caption-input"
            />
          </div>

          <div>
            <label className="text-sm text-slate-400">Hashtags (space-separated)</label>
            <input
              type="text"
              value={editedPost.hashtags.join(' ')}
              onChange={(e) => handleHashtagChange(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white mt-1"
              data-testid="edit-hashtags-input"
            />
          </div>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(editedPost)}
            data-testid="save-edit-btn"
            className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium"
          >
            Save Changes
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default WeeklyContentPlanner;
